import AppForms from "@toothfairy/shared-ui/AppForms";
import AppTextInput from "@toothfairy/shared-ui/AppTextInput";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Platform,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { AppText } from "@toothfairy/shared-ui";
import ToothFairyModal from "./ToothFairyModal";
import businessEmojis from "./businessEmojis";

const EmojiPickerField = ({
  name,
  label,
  placeholder = "Select or type an emoji",
  showTextInputLabel = true,
  wrapperInputStyle,
  textContainer,
  errorOffset,
  itemFontSize = 30,
}) => {
  const { currentTheme } = AppTheme.useTheme();
  const { t } = useTranslation();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [search, setSearch] = useState("");
  const { values, setFieldValue } = AppForms.useFormikContext();
  const currentValue = values?.[name] || "";

  const handleEmojiSelect = useCallback(
    (emoji) => {
      setFieldValue(name, emoji);
      setIsModalVisible(false);
    },
    [name, setFieldValue]
  );

  const handleOpenModal = useCallback(() => {
    setSearch("");
    setIsModalVisible(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalVisible(false);
  }, []);

  const filteredEmojis = useMemo(() => {
    const query = (search || "").trim().toLowerCase();
    const seen = new Set();
    const unique = businessEmojis.filter((e) => {
      if (seen.has(e.emoji)) return false;
      seen.add(e.emoji);
      return true;
    });
    if (!query) return unique;
    return unique.filter(
      (e) =>
        e.emoji.includes(query) ||
        e.keywords.some((k) => k.toLowerCase().includes(query))
    );
  }, [search]);

  const styles = StyleSheet.create({
    container: {
      position: "relative",
    },
    emojiButtonContainer: {
      position: Platform.OS === "web" ? "absolute" : "relative",
      right: 10,
      top: Platform.OS === "web" ? 46 : 0,
      zIndex: 1,
    },
    emojiButton: {
      width: 30,
      height: 30,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: currentTheme?.theme?.border_color,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: currentTheme?.theme?.inputBackgroundColor,
    },
    modalContent: {
      flex: 1,
      minHeight: 300,
    },
    searchWrapper: {
      marginBottom: 12,
    },
    gridWrapper: {
      flexDirection: "row",
      flexWrap: "wrap",
    },
    emojiCell: {
      padding: 8,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 8,
    },
    emptyState: {
      paddingVertical: 24,
      alignItems: "center",
    },
  });

  return (
    <View style={styles.container}>
      <AppForms.AppFormField
        showTextInputLabel={showTextInputLabel}
        wrapperInputStyle={wrapperInputStyle}
        textContainer={textContainer}
        errorOffset={errorOffset}
        label={label}
        placeholder={placeholder}
        keyboardtype="default"
        textContextType="label"
        autoCorrect={false}
        name={name}
        maxCharacters={8}
      />
      <View style={styles.emojiButtonContainer}>
        <TouchableOpacity
          style={styles.emojiButton}
          onPress={handleOpenModal}
          accessibilityLabel={t("settings.list.chooseIcon")}
          accessibilityRole="button"
        >
          {currentValue ? (
            <AppText style={{ fontSize: 18 }}>{currentValue}</AppText>
          ) : (
            <AppText style={{ fontSize: 14 }}>😀</AppText>
          )}
        </TouchableOpacity>
      </View>
      <ToothFairyModal
        isModalOpen={isModalVisible}
        setIsModalOpen={setIsModalVisible}
        modalTitle={t("settings.list.chooseIcon")}
        closeModal={handleCloseModal}
      >
        <View style={styles.modalContent}>
          <View style={styles.searchWrapper}>
            <AppTextInput
              placeholder={t("settings.list.searchIcons", {
                defaultValue: "Search icons…",
              })}
              value={search}
              onChangeText={setSearch}
              autoCorrect={false}
              autoCapitalize="none"
            />
          </View>
          <ScrollView style={{ flex: 1 }} keyboardShouldPersistTaps="handled">
            <View style={styles.gridWrapper}>
              {filteredEmojis.map((item, index) => (
                <TouchableOpacity
                  key={`${item.emoji}-${index}`}
                  onPress={() => handleEmojiSelect(item.emoji)}
                  style={styles.emojiCell}
                >
                  <AppText style={{ fontSize: itemFontSize }}>
                    {item.emoji}
                  </AppText>
                </TouchableOpacity>
              ))}
            </View>
            {filteredEmojis.length === 0 && (
              <View style={styles.emptyState}>
                <AppText color={currentTheme?.theme?.mediumGrey}>
                  {t("settings.list.noIconsFound", {
                    defaultValue: "No icons match your search",
                  })}
                </AppText>
              </View>
            )}
          </ScrollView>
        </View>
      </ToothFairyModal>
    </View>
  );
};

export default EmojiPickerField;
